// ============================================
// AuthContext: Authentication & current user
// Uses Supabase Auth for login/signup,
// links to TeamMember profiles in KV store.
// ============================================

import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { createClient } from '@supabase/supabase-js';
import { projectId, publicAnonKey } from '/utils/supabase/info';
import type { TeamMember } from './mockData';

const supabase = createClient(
  `https://${projectId}.supabase.co`,
  publicAnonKey,
);

const API_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-43ef46c1/api`;

// How long the initial auth check may block the UI before AuthGate falls
// through to the login screen (state still updates if a session arrives late).
const INIT_TIMEOUT_MS = 10_000;

// Retry wrapper for transient network failures (cold-start edge functions).
// Every attempt carries its own timeout: fetch() only rejects on network
// errors, so a request that stalls without failing would otherwise block
// the auth bootstrap — and the UI behind it — indefinitely.
async function fetchWithRetry(input: RequestInfo | URL, init?: RequestInit, retries = 2, attemptTimeoutMs = 12_000): Promise<Response> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fetch(input, { ...init, signal: AbortSignal.timeout(attemptTimeoutMs) });
    } catch (err) {
      if (attempt === retries) throw err;
      await new Promise(r => setTimeout(r, 500 + attempt * 1000));
      console.warn(`Auth fetch retry ${attempt + 1}/${retries}`);
    }
  }
  throw new Error('Unreachable');
}

interface AuthContextType {
  currentUser: TeamMember | null;
  accessToken: string | null;
  isAdmin: boolean;
  isLoggedIn: boolean;
  isLoading: boolean;
  /** True when a Supabase session exists, even if no team member profile matched it. */
  hasSession: boolean;
  /** Email on the Supabase session — shown on the no-access screen. */
  sessionEmail: string | null;
  /** Session exists but no team member profile matches it (e.g. a Google
   *  account whose email isn't on the team roster). */
  noProfile: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  /** Re-check the roster for the current session (used by the no-access screen). */
  refreshProfile: () => Promise<boolean>;
  clearError: () => void;
}

interface RegisterData {
  email: string;
  password: string;
  name: string;
  role?: 'admin' | 'member';
  title?: string;
  departments?: string[];
  avatarColor?: string;
  phone?: string;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<TeamMember | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasSession, setHasSession] = useState(false);
  const [sessionEmail, setSessionEmail] = useState<string | null>(null);
  const [noProfile, setNoProfile] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Token of the most recently observed session — lets in-flight profile
  // fetches detect they're stale without asking the auth client (whose
  // calls funnel through a cross-tab lock and must be kept off this path).
  const latestTokenRef = useRef<string | null>(null);
  // Supabase user id the loaded profile belongs to, so repeat SIGNED_IN
  // events (supabase-js re-emits on tab refocus) don't refetch the roster.
  const profileUserIdRef = useRef<string | null>(null);

  // Fetch the team member profile for a given access token
  const fetchProfile = useCallback(async (token: string, userId?: string) => {
    try {
      const res = await fetchWithRetry(`${API_BASE}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      // A slow response must not clobber state from a newer sign-in or a
      // sign-out that happened while it was in flight — only apply state
      // if this token still belongs to the active session.
      if (latestTokenRef.current !== token) {
        return false;
      }

      if (res.ok) {
        const data = await res.json();
        setCurrentUser(data.teamMember);
        setAccessToken(token);
        setNoProfile(false);
        if (userId) profileUserIdRef.current = userId;
        return true;
      }
      if (res.status === 404) {
        // Valid session, but no team member on the roster matches this
        // account — AuthGate shows the no-access screen for this state.
        setCurrentUser(null);
        setNoProfile(true);
      }
      return false;
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      return false;
    }
  }, []);

  // Session bootstrap. INITIAL_SESSION always fires once the client has
  // restored (or failed to restore) a persisted session, so the listener is
  // the single source of truth — no separate getSession() startup call.
  useEffect(() => {
    let cancelled = false;

    // Failsafe: supabase-js serializes auth work through a navigator.locks
    // Web Lock shared by every tab on this origin. A stalled token-refresh
    // request — or another tab wedging that lock — can delay INITIAL_SESSION
    // indefinitely, which used to leave the app on a blank white page. Stop
    // blocking the UI after a grace period; if a session materializes later,
    // the listener below still applies it.
    const watchdog = setTimeout(() => {
      if (!cancelled) {
        console.warn('[StageFlow] Auth initialization timed out — showing login screen while it retries in the background');
        setIsLoading(false);
      }
    }, INIT_TIMEOUT_MS);

    const finishInitialLoad = () => {
      clearTimeout(watchdog);
      if (!cancelled) setIsLoading(false);
    };

    // Also fires when Supabase consumes the OAuth token fragment after the
    // Google redirect lands on /auth/callback.
    //
    // The callback must stay synchronous: supabase-js invokes it while
    // holding the auth lock, so awaiting another supabase.auth.* call in
    // here can deadlock every later auth call in the app (the other cause
    // of the blank page on load). Async work is deferred to the next tick.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (cancelled) return;

        const token = session?.access_token ?? null;
        latestTokenRef.current = token;
        setHasSession(!!session);
        setSessionEmail(session?.user?.email ?? null);

        if (!token) {
          profileUserIdRef.current = null;
          setCurrentUser(null);
          setAccessToken(null);
          setNoProfile(false);
          if (event === 'INITIAL_SESSION') finishInitialLoad();
          return;
        }

        if (event === 'TOKEN_REFRESHED' || profileUserIdRef.current === session!.user.id) {
          // Same signed-in user — keep API calls on the fresh token and
          // skip the roster refetch.
          setAccessToken(token);
          if (event === 'INITIAL_SESSION') finishInitialLoad();
          return;
        }

        const userId = session!.user.id;
        setTimeout(() => {
          if (cancelled) return;
          fetchProfile(token, userId).finally(() => {
            if (event === 'INITIAL_SESSION') finishInitialLoad();
          });
        }, 0);
      }
    );

    return () => {
      cancelled = true;
      clearTimeout(watchdog);
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const login = async (email: string, password: string) => {
    setError(null);
    try {
      const { data, error: loginError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (loginError) {
        throw new Error(loginError.message);
      }
      if (data.session?.access_token) {
        latestTokenRef.current = data.session.access_token;
        const found = await fetchProfile(data.session.access_token, data.session.user.id);
        if (!found) {
          throw new Error('No team member profile found for this account. Contact your admin.');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Login failed');
      throw err;
    }
  };

  // Google OAuth — redirects the browser to Google, then back to
  // /auth/callback where Supabase picks up the session. First-time Google
  // sign-ins are matched to an existing team member by email: Supabase
  // auto-links the Google identity to a password account with the same
  // verified email, and the server's /auth/me falls back to a
  // case-insensitive roster email match before giving up.
  const loginWithGoogle = async () => {
    setError(null);
    try {
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (oauthError) {
        throw new Error(oauthError.message);
      }
      // On success the browser navigates away to Google — nothing more to do.
    } catch (err: any) {
      setError(err.message || 'Google sign-in failed');
      throw err;
    }
  };

  const logout = async () => {
    try {
      // signOut also goes through the shared auth lock — cap the wait so a
      // wedged lock can't leave the user stuck in a signed-in shell.
      await Promise.race([
        supabase.auth.signOut(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Sign out timed out')), 5000)),
      ]);
    } catch (err) {
      // Even if the server-side revoke fails (e.g. offline), fall through
      // and clear local state so the UI never sticks in a signed-in shell.
      console.error('Sign out error:', err);
    } finally {
      latestTokenRef.current = null;
      profileUserIdRef.current = null;
      setCurrentUser(null);
      setAccessToken(null);
      setHasSession(false);
      setSessionEmail(null);
      setNoProfile(false);
    }
  };

  // Re-run the roster lookup for the current session — lets the no-access
  // screen pick up a profile an admin just created without a fresh sign-in.
  const refreshProfile = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return false;
    latestTokenRef.current = session.access_token;
    return fetchProfile(session.access_token, session.user.id);
  }, [fetchProfile]);

  const register = async (data: RegisterData) => {
    setError(null);
    try {
      const res = await fetchWithRetry(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${publicAnonKey}`,
        },
        body: JSON.stringify(data),
      });
      const result = await res.json();
      if (!res.ok) {
        throw new Error(result.error || 'Registration failed');
      }
      // After registration, log in automatically
      await login(data.email, data.password);
    } catch (err: any) {
      setError(err.message || 'Registration failed');
      throw err;
    }
  };

  const clearError = () => setError(null);

  return (
    <AuthContext.Provider value={{
      currentUser,
      accessToken,
      isAdmin: currentUser?.role === 'admin',
      isLoggedIn: !!currentUser,
      isLoading,
      hasSession,
      sessionEmail,
      noProfile,
      error,
      login,
      loginWithGoogle,
      logout,
      register,
      refreshProfile,
      clearError,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}